import { Navigate } from 'react-router-dom';
import { useAccessControl, type FeatureKey } from '@/hooks/useAccessControl';
import { Loader2 } from 'lucide-react';

interface FeatureRouteProps {
  feature: FeatureKey;
  children: React.ReactNode;
  /** Where to redirect when blocked (default: /) */
  redirectTo?: string;
}

/**
 * Route-level feature gate. If the feature is disabled/blocked,
 * redirects to home (or auth). Does NOT render children at all.
 */
export default function FeatureRoute({ feature, children, redirectTo }: FeatureRouteProps) {
  const { canAccessFeature, loading, isGuest } = useAccessControl();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const decision = canAccessFeature(feature);
  if (!decision.allowed) {
    // If blocked because not authenticated, redirect to auth
    if (decision.reason === 'not_authenticated') {
      return <Navigate to="/auth" replace />;
    }
    return <Navigate to={redirectTo ?? '/'} replace />;
  }

  return <>{children}</>;
}
